const BlockedRandomization = require('./blocked');
const { randomInt, shuffle } = require('../math/seed-rng');
const {
  validateBlockSize,
  validateBlockSizes,
  validateCompleteStratumBlocks,
  getSmallStratumWarnings,
  validateStrata
} = require('../math/validation');

class StratifiedBlockRandomization extends BlockedRandomization {
  constructor(id, name, mode, options = {}) {
    super();
    this.id = id;
    this.name = name;
    this.mode = mode;
    this.strataInputName = options.strataInputName || 'strataSizes';
    this.strataLabel = options.strataLabel || 'Stratum';
    this.copyStratumToCluster = Boolean(options.copyStratumToCluster);
  }

  validateParams(params) {
    params.strata = validateStrata(params[this.strataInputName], params.n);
    params.methodWarnings = getSmallStratumWarnings(params.strata, params.arms, this.strataLabel);

    if (this.mode === 'fixed') {
      params.blockSize = validateBlockSize(params.blockSize || params.arms * 2, params.arms);
      validateCompleteStratumBlocks(params.strata, params.blockSize);
      return;
    }

    params.blockSizes = validateBlockSizes(params.blockSizes || `${params.arms * 2},${params.arms * 3}`, params.arms);
  }

  selectResultParameters(params) {
    const result = {
      studyName: params.studyName || '',
      n: params.n,
      arms: params.arms,
      armLabels: params.armLabels,
      seed: params.seed,
      technique: params.technique,
      strata: params.strata,
      strataInput: params[this.strataInputName],
      randomizationUnit: 'participant',
      balanceUnit: this.copyStratumToCluster ? 'cluster-as-stratum' : 'stratum'
    };

    if (params.methodWarnings.length > 0) {
      result.methodWarnings = params.methodWarnings;
    }

    if (this.mode === 'fixed') {
      result.blockSize = params.blockSize;
    } else {
      result.blockSizes = params.blockSizes;
    }

    return result;
  }

  randomize(rng, params) {
    const sequence = [];
    let patientIndex = 0;
    let blockNumber = 1;

    params.strata.forEach((stratum) => {
      let stratumIndex = 0;

      while (stratumIndex < stratum.size) {
        const blockSize = this.mode === 'fixed'
          ? params.blockSize
          : params.blockSizes[randomInt(rng, 0, params.blockSizes.length)];
        const blockArms = this.buildBalancedBlock(params.arms, blockSize);
        const randomizedBlock = shuffle(blockArms, rng);

        for (const armIndex of randomizedBlock) {
          if (stratumIndex >= stratum.size) {
            break;
          }

          const extras = {
            blockNumber,
            blockSize,
            stratum: stratum.id
          };

          if (this.copyStratumToCluster) {
            extras.cluster = stratum.id;
          }

          sequence.push(this.buildAssignment(patientIndex, armIndex, extras));
          patientIndex += 1;
          stratumIndex += 1;
        }

        blockNumber += 1;
      }
    });

    return sequence;
  }

  getExplanation() {
    const blockText = this.mode === 'fixed'
      ? 'a fixed block size inside each stratum'
      : 'a seeded random block size inside each stratum';
    const unitText = this.copyStratumToCluster
      ? 'The cluster/center is used as a stratum only; participants inside the same cluster can receive different arms.'
      : 'Each stratum is balanced independently while participants remain the unit of randomization.';

    return `
      <h3>${this.name}</h3>
      <p>Participants are grouped by ${this.strataLabel.toLowerCase()} and randomized with ${blockText}.</p>
      <pre>For each ${this.strataLabel.toLowerCase()}:
  create balanced blocks
  shuffle assignments using seed
  assign participants inside that ${this.strataLabel.toLowerCase()}</pre>
      <p>${unitText}</p>
    `;
  }
}

module.exports = StratifiedBlockRandomization;
